import { useTheme } from '@emotion/react';
import React, { useRef } from 'react';

import { Alert, Box, Grid, ScrollBox, Text } from '~/components/blocks';
import {
  DispensingRequestCollapse,
  InsuranceCardCollapse,
  QuestionnaireSheet,
} from '~/components/partials';
import { OutpatientQuestionnaireSheet } from '~/components/partials/OutpatientQuestionnaireSheet';
import { MedixsPatientProfile } from '~/components/partials/PatientDetailDialog/PatientPane/MedixsPatientProfile';
import { PrescriptionCollapseFallback } from '~/components/partials/PatientDetailDialog/PatientPane/PrescriptionCollapseFallback';
import { PrescriptionCollapse } from '~/components/partials/PrescriptionCollapse';
import { MedixsPatientDetailDialogProfileFragment } from '~/graphql/generated/graphql';

type Props = {
  medixsPatient: MedixsPatientDetailDialogProfileFragment;
};

export const MedixsPatientKarte = React.memo((props: Props) => {
  const { medixsPatient } = props;
  const theme = useTheme();
  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);

  const appointment = medixsPatient.latestAppointment;
  const visitDay = medixsPatient.visitedDate;
  const questionnaireSheet = medixsPatient.latestQuestionnaireSheet;

  return (
    <Grid height="100%" overflow="auto" gridTemplateRows="min-content min-content 1fr">
      <MedixsPatientProfile patient={medixsPatient} visitedAt={visitDay} />
      <Box marginTop={theme.space.m}>
        {appointment?.patient && (
          <DispensingRequestCollapse printable={false} appointmentId={appointment.id} />
        )}
        {(appointment?.webVisitor || appointment?.lineVisitor) && (
          <PrescriptionCollapse appointmentId={appointment.id} />
        )}
        {!appointment && <PrescriptionCollapseFallback />}
        {medixsPatient.patient && (
          <InsuranceCardCollapse
            patientId={medixsPatient.patient?.id}
            isActivePatient={medixsPatient.patient?.active}
            qualificationConfirmationMethod={
              appointment?.qualificationConfirmationMethod || 'insurance_card'
            }
          />
        )}
      </Box>
      <ScrollBox ref={scrollRef}>
        <Box marginTop={theme.space.l}>
          <Text fontWeight="bold" marginBottom={theme.space.m}>
            問診
          </Text>
          {questionnaireSheet?.__typename === 'OutpatientQuestionnaireEntry' && (
            <OutpatientQuestionnaireSheet
              answers={questionnaireSheet.questionnaires}
              enteredAt={questionnaireSheet.enteredAt}
            />
          )}
          {questionnaireSheet?.__typename === 'QuestionnaireSheet' && (
            <QuestionnaireSheet loading={false} sheet={questionnaireSheet} />
          )}
          {!questionnaireSheet && <Alert status="info">登録患者のため、問診票はありません</Alert>}
        </Box>
      </ScrollBox>
    </Grid>
  );
});

MedixsPatientKarte.displayName = 'MedixsPatientKarte';
